/**
 * YUMIX — Day7CelebrationModal
 *
 * Auto-opens once on Dashboard mount when the user reaches account_age >= 7
 * days (first eligibility). Shows a festive bilan + their first grade.
 *
 * Pulls data from GET /welcome-challenge/day7-summary :
 *   - First call : computes stats, attributes grade, sends SendGrid email
 *   - Subsequent calls : returns the stored summary, no email re-send
 *
 * Acknowledged via POST /welcome-challenge/day7-summary/acknowledge so it
 * doesn't re-open after dismissal.
 */
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '../context/AppContext';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Flame, Award, Utensils, Calendar, Activity, TrendingUp, Sparkles, Share2 } from 'lucide-react';
import { playCelebrationCue } from '../utils/celebrationSound';
import { shareGradeCard } from '../utils/gradeShareCard';
import { toast } from 'sonner';

const Day7CelebrationModal = ({ language = 'fr' }) => {
  const fr = language.startsWith('fr');
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState(null);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/welcome-challenge/day7-summary');
        if (cancelled) return;
        if (!res.data?.available) return;
        const s = res.data.summary;
        // Only auto-open if it's the first view OR the user hasn't acknowledged yet
        const shouldOpen = res.data.is_first_view || !s.acknowledged_at;
        if (shouldOpen) {
          setSummary(s);
          setOpen(true);
          // Audio celebration on first view (best-effort)
          try { playCelebrationCue?.(); } catch { /* silent */ }
        }
      } catch {
        /* silent — modal is non-critical */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleClose = async () => {
    setOpen(false);
    try {
      await api.post('/welcome-challenge/day7-summary/acknowledge');
    } catch { /* silent */ }
  };

  const handleShare = async () => {
    if (!summary) return;
    setSharing(true);
    try {
      const result = await shareGradeCard({
        userName: summary.name || 'Membre',
        gradeLabel: fr ? summary.grade?.label_fr : summary.grade?.label_en,
        stats: summary.stats || {},
        language,
      });
      if (result?.method === 'web_share') {
        toast.success(fr ? 'Partage envoyé · merci !' : 'Shared · thank you!');
      } else if (result?.method === 'download') {
        toast.success(fr ? 'Carte téléchargée · partage-la sur tes Stories' : 'Card downloaded · share it on your Stories');
      }
    } catch (err) {
      toast.error(err?.message || (fr ? 'Erreur de partage' : 'Share failed'));
    } finally {
      setSharing(false);
    }
  };

  if (!summary) return null;

  const stats = summary.stats || {};
  const grade = summary.grade || {};
  const noPaywall = summary.no_paywall_warm_up;
  const gradeLabel = fr ? grade.label_fr : grade.label_en;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent
        className="max-w-md p-0 overflow-hidden border-orange-500/40 bg-gradient-to-b from-background via-orange-950/15 to-background"
        data-testid="day7-celebration-modal"
      >
        {/* Hero band with confetti */}
        <div className="relative bg-gradient-to-br from-orange-500 via-orange-600 to-amber-500 px-6 pt-8 pb-6 text-center overflow-hidden">
          <AnimatePresence>
            {[...Array(12)].map((_, i) => (
              <motion.span
                key={i}
                initial={{ y: -40, opacity: 0, x: (i - 6) * 18 }}
                animate={{ y: 200, opacity: [0, 1, 0], rotate: 360 }}
                transition={{ duration: 1.6, delay: i * 0.08, ease: 'easeOut' }}
                className="absolute top-2 left-1/2 text-lg pointer-events-none"
                aria-hidden
              >
                {['🎉','🔥','⭐','💥','✨'][i % 5]}
              </motion.span>
            ))}
          </AnimatePresence>
          <motion.div
            initial={{ scale: 0.6, rotate: -10 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: 'spring', stiffness: 220, damping: 12 }}
            className="text-6xl mb-2 inline-block"
          >
            🔥
          </motion.div>
          <h2 className="font-heading text-xl uppercase tracking-tight text-white">
            {fr ? 'Défi 7 Jours Complété !' : '7-Day Challenge Complete!'}
          </h2>
          <p className="text-white/85 text-xs mt-1">
            {fr ? 'Première semaine dans la meute YUMIX' : 'First week in the YUMIX pack'}
          </p>
        </div>

        {/* Grade */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mx-5 mt-4 rounded-xl border border-orange-500/40 bg-orange-500/5 px-5 py-4 text-center"
          data-testid="day7-grade-card"
        >
          <div className="flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">
            <Award className="w-3 h-3" />
            {fr ? 'Ton grade officiel' : 'Your official grade'}
          </div>
          <p className="font-heading text-2xl text-orange-400 tracking-tight" data-testid="day7-grade-label">
            {gradeLabel}
          </p>
        </motion.div>

        {/* Stats */}
        <div className="mx-5 mt-4 grid grid-cols-3 gap-2">
          <StatTile icon={Calendar} value={stats.active_days || 0} label={fr ? 'Jours actifs' : 'Active days'} delay={0.4} />
          <StatTile icon={Utensils} value={stats.meals_logged || 0} label={fr ? 'Repas logués' : 'Meals logged'} delay={0.5} />
          <StatTile icon={TrendingUp} value={`${stats.consistency_pct || 0}%`} label={fr ? 'Constance' : 'Consistency'} delay={0.6} />
        </div>
        {(stats.workouts_logged || 0) > 0 && (
          <p className="text-center text-[11px] text-muted-foreground mt-3 flex items-center justify-center gap-1">
            <Activity className="w-3 h-3" />
            {stats.workouts_logged} {fr ? 'entraînement(s) loggué(s)' : 'workout(s) logged'}
          </p>
        )}

        {/* Coach message */}
        <div className="mx-5 mt-4 rounded-lg border-l-2 border-orange-500/60 bg-secondary/40 px-4 py-3">
          <p className="text-[12px] leading-relaxed text-foreground">
            {fr
              ? <>La <strong>constance</strong>, c'est tout. Pas la perfection. Tu logues, tu reviens, tu logues encore. Cette semaine 2, on <strong>amplifie</strong>.</>
              : <>It's all about <strong>consistency</strong>, not perfection. Log, come back, log again. Week 2 — let's <strong>amplify</strong>.</>}
          </p>
          <p className="text-[11px] text-orange-400 font-bold mt-2">— L'équipe YUMIX</p>
        </div>

        {/* CTA */}
        <div className="px-5 pt-4 pb-5 space-y-2">
          {!noPaywall && (
            <p className="text-center text-[10px] text-muted-foreground uppercase tracking-wider flex items-center justify-center gap-1">
              <Sparkles className="w-3 h-3 text-orange-400" />
              {fr ? 'Semaine 2 — encore 7 jours pour cristalliser l\'habitude' : 'Week 2 — 7 more days to crystallize the habit'}
            </p>
          )}
          <Button
            onClick={handleShare}
            disabled={sharing}
            variant="outline"
            className="w-full border-orange-500/50 text-orange-300 hover:bg-orange-500/10 font-heading uppercase tracking-wider text-[12px]"
            data-testid="day7-share-grade-btn"
          >
            <Share2 className="w-4 h-4 mr-2" />
            {sharing
              ? (fr ? 'Préparation...' : 'Preparing...')
              : (fr ? 'Partager mon grade' : 'Share my grade')}
          </Button>
          <Button
            onClick={handleClose}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white font-heading uppercase tracking-wider text-[12px]"
            data-testid="day7-close-btn"
          >
            <Flame className="w-4 h-4 mr-2" />
            {fr ? 'Attaquer la semaine 2' : 'Attack week 2'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const StatTile = ({ icon: Icon, value, label, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    className="rounded-lg border border-border/60 bg-secondary/30 px-2 py-3 text-center"
  >
    <Icon className="w-3.5 h-3.5 mx-auto text-orange-400 mb-1" />
    <p className="font-black text-xl text-foreground tabular-nums">{value}</p>
    <p className="text-[9px] uppercase tracking-wider text-muted-foreground mt-0.5">{label}</p>
  </motion.div>
);

export default Day7CelebrationModal;
